import { createPortal } from 'react-dom';
import type { Folder, MessageSummary } from '@/api';
import { Icon } from '@/components/Icon';
import {
  flattenMoveTargets,
  mailboxMoveItems,
  COPY_TO_SUBMENU_ID,
  MOVE_TO_SUBMENU_ID,
  type MailboxFolders,
  type MenuActionNode,
  type MenuNode,
} from '@/features/context-menu';
import { OFFLINE_TRANSFER_REASON, type DropMode } from '@/features/dnd/messageDrag';

/**
 * Multi-select's two pieces of chrome (doc 06 §5; the owner: "long press for
 * multi-select and a button in the bottom right for actions").
 *
 * - `SelectionBar`: ✕ · "N selected" · Select all. On handheld it *replaces*
 *   the app bar — same place, same height, portalled to `<body>` and fixed at
 *   the top, with the shell's bar hidden under it (mail-list.css) — because
 *   search and the drawer mean nothing mid-selection and Gmail does the same.
 *   On desktop it sits in the list pane where the filter chips were, and
 *   carries its own Actions button, since there is no FAB there.
 * - `bulkMenuItems`: the verbs the actions sheet (handheld) or popover
 *   (desktop, and a right-click inside the selection) offers. The menu itself
 *   is the context menu's `Menu`, so the sheet, the Move-to submenu with its
 *   filter, the roving focus and every close path are the ones ADR 0019
 *   already tested.
 *
 * "Select all" means *loaded* rows. The contract's bulk endpoints take ids,
 * never a folder-wide selector (doc 06 §5), so selecting mail the client has
 * not fetched would either be a lie or a hidden page-by-page crawl. The bar
 * says "All 50 loaded" when that is what is selected and more exist, and
 * nothing is ever done to a row that is not on the list.
 */

export interface SelectionBarProps {
  count: number;
  /** Every loaded row is selected. */
  allLoaded: boolean;
  /** The folder has rows beyond what is loaded. */
  hasMore: boolean;
  onCancel: () => void;
  onSelectAll: () => void;
  /** Desktop only: the Actions button. Handheld's is the FAB. */
  onActions?: ((anchor: DOMRect) => void) | undefined;
  variant: 'appbar' | 'inline';
}

export function SelectionBar({
  count,
  allLoaded,
  hasMore,
  onCancel,
  onSelectAll,
  onActions,
  variant,
}: SelectionBarProps) {
  const partial = allLoaded && hasMore;
  const bar = (
    <div className="selection-bar" data-variant={variant} role="toolbar" aria-label="Selection">
      <button
        type="button"
        className="btn btn--icon selection-bar__cancel"
        aria-label="Cancel selection"
        onClick={onCancel}
      >
        <Icon name="close" />
      </button>
      <span className="selection-bar__count">
        {partial ? `All ${count} loaded` : `${count} selected`}
        {/* What "all" did not include, said once where it matters. */}
        {partial && (
          <span className="visually-hidden">
            {' '}
            messages selected. Messages further down that are not loaded yet are not included.
          </span>
        )}
      </span>
      <span className="spacer" />
      {/* Absent rather than disabled once everything loaded is selected. */}
      {!allLoaded && (
        <button
          type="button"
          className="btn btn--ghost selection-bar__all"
          aria-label={hasMore ? 'Select all loaded messages' : 'Select all messages'}
          onClick={onSelectAll}
        >
          Select all
        </button>
      )}
      {onActions && (
        <button
          type="button"
          className="btn selection-bar__actions"
          aria-haspopup="menu"
          onClick={(event) => onActions(event.currentTarget.getBoundingClientRect())}
        >
          Actions <Icon name="chevron-down" size={14} />
        </button>
      )}
    </div>
  );
  // In place on desktop; on handheld over the app bar, which is the shell's
  // and sticky, so it is covered rather than re-rendered from here.
  return variant === 'appbar' ? createPortal(bar, document.body) : bar;
}

export interface BulkActions {
  archive: () => void;
  remove: () => void;
  setRead: (read: boolean) => void;
  setStarred: (starred: boolean) => void;
  move: (folderId: string) => void;
  /**
   * Copy them into a folder of their own mailbox. Optional: unwired, there is
   * no Copy to. With `transfer` and several mailboxes, Copy to lists every
   * mailbox's folders instead, and every choice goes through `transfer`.
   */
  copy?: ((folderId: string) => void) | undefined;
  /**
   * Into a folder of any mailbox (ADR 0108): rows of that mailbox are moved
   * as usual, the rest are transferred. With it and more than one mailbox in
   * `options.mailboxes`, Move to lists every mailbox's folders, grouped.
   */
  transfer?: ((accountId: string, folderId: string, mode: DropMode) => void) | undefined;
  /**
   * Report spam / Not spam for the batch. The caller passes at most one: Not
   * spam only when EVERY selected row is in its mailbox's Junk, Report spam
   * otherwise — and neither when there is no Junk folder to report into.
   */
  reportSpam?: (() => void) | undefined;
  notSpam?: (() => void) | undefined;
  /** One task per selected message (ADR 0075): opens the form. */
  addToTasks?: (() => void) | undefined;
  /** Snooze them all (ADR 0076): one request per mailbox. */
  snooze?: (() => void) | undefined;
  /** Label them (plan/17): opens the picker. One mailbox only — see below. */
  labelAs?: (() => void) | undefined;
  /** Exactly one selected: the full single-message menu (Reply, Forward, Notify…). */
  moreActions?: (() => void) | undefined;
}

/**
 * The bulk verbs, in the context menu's order and grouping (ADR 0019 §2.1).
 *
 * Absent, not disabled, as there: Archive is not offered in Archive, "Mark as
 * read" only when something selected is unread, and so on. A mixed selection
 * gets both halves of a pair — which is the one honest answer to "star these"
 * when some already are. Reply and Forward are single-message verbs and are
 * absent for any N; with N = 1 "More actions…" is the way to them.
 */
export function bulkMenuItems(
  messages: readonly MessageSummary[],
  folders: Folder[],
  currentFolderId: string | undefined,
  actions: BulkActions,
  { mailboxes, online = true }: { mailboxes?: readonly MailboxFolders[] | undefined; online?: boolean } = {},
): MenuNode[] {
  const anyUnread = messages.some((message) => !message.flags.includes('seen'));
  const anyRead = messages.some((message) => message.flags.includes('seen'));
  const anyUnstarred = messages.some((message) => !message.flags.includes('flagged'));
  const anyStarred = messages.some((message) => message.flags.includes('flagged'));
  const archiveFolder = folders.find((folder) => folder.role === 'archive');
  const inArchive = archiveFolder !== undefined && archiveFolder.id === currentFolderId;

  const nodes: MenuNode[] = [];
  // Absent on a mailbox with no Archive folder as well as inside Archive: the
  // archive endpoint answers 404 there, so the verb cannot do what it says.
  if (archiveFolder !== undefined && !inArchive) {
    nodes.push({ kind: 'item', id: 'archive', label: 'Archive', shortcut: 'E', onSelect: actions.archive });
  }
  nodes.push({ kind: 'item', id: 'delete', label: 'Delete', shortcut: 'Del', onSelect: actions.remove });
  if (actions.notSpam) {
    nodes.push({ kind: 'item', id: 'not-spam', label: 'Not spam', shortcut: '!', onSelect: actions.notSpam });
  } else if (actions.reportSpam) {
    nodes.push({ kind: 'item', id: 'report-spam', label: 'Report spam', shortcut: '!', onSelect: actions.reportSpam });
  }
  nodes.push({ kind: 'separator', id: 'sep-state' });
  if (anyUnread) {
    nodes.push({ kind: 'item', id: 'read', label: 'Mark as read', shortcut: 'Shift+I', onSelect: () => actions.setRead(true) });
  }
  if (anyRead) {
    nodes.push({ kind: 'item', id: 'unread', label: 'Mark as unread', shortcut: 'Shift+U', onSelect: () => actions.setRead(false) });
  }
  if (anyUnstarred) {
    nodes.push({ kind: 'item', id: 'star', label: 'Star', shortcut: 'S', onSelect: () => actions.setStarred(true) });
  }
  if (anyStarred) {
    nodes.push({ kind: 'item', id: 'unstar', label: 'Remove star', onSelect: () => actions.setStarred(false) });
  }
  // Every mailbox has Tasks, so unlike Move to this spans a unified selection:
  // each task goes to its own message's mailbox.
  if (actions.addToTasks) {
    nodes.push({ kind: 'item', id: 'add-to-tasks', label: 'Add to Tasks', shortcut: 'Shift+T', onSelect: actions.addToTasks });
  }
  // Also spans a unified selection: each mailbox is asked for its own messages.
  if (actions.snooze) {
    nodes.push({ kind: 'item', id: 'snooze', label: 'Snooze…', shortcut: 'B', onSelect: actions.snooze });
  }

  /*
   * Label as… (plan/17). A label belongs to ONE mailbox, like a folder, so a
   * selection from several is refused the way Move to is: the item stays and
   * says why, rather than offering one mailbox's labels for another's mail.
   */
  const labelMailboxes = new Set(messages.map((message) => message.accountId));
  if (actions.labelAs) {
    nodes.push(
      labelMailboxes.size > 1
        ? {
            kind: 'item',
            id: 'label-as',
            label: 'Label as…',
            shortcut: 'L',
            disabled: true,
            hint: `Needs one mailbox — these ${messages.length} are from ${labelMailboxes.size}, and a label belongs to a single account.`,
            onSelect: () => {},
          }
        : { kind: 'item', id: 'label-as', label: 'Label as…', shortcut: 'L', onSelect: actions.labelAs },
    );
  }

  /*
   * Move to… across mailboxes (ADR 0108, which replaced ADR 0056 §4's
   * refusal). With several mailboxes the list is every mailbox's folders,
   * grouped by mailbox, and a selection from several mailboxes can go to any
   * of them: rows already in the target's mailbox are moved as usual, the
   * rest are transferred. Without the transfer (or with one mailbox) a
   * selection spanning two still says why it cannot be moved.
   */
  const sourceAccounts = new Set(messages.map((message) => message.accountId));
  const transfer = actions.transfer;
  if (transfer !== undefined && mailboxes !== undefined && mailboxes.length > 1) {
    const [only] = sourceAccounts;
    const items = mailboxMoveItems({
      mailboxes,
      sources: sourceAccounts,
      current:
        sourceAccounts.size === 1 && only !== undefined ? { accountId: only, folderId: currentFolderId } : undefined,
      online,
      offlineReason: OFFLINE_TRANSFER_REASON,
      idPrefix: 'move',
    }).map(({ accountId, folderId, ...item }) => ({
      kind: 'item' as const,
      ...item,
      onSelect: () => transfer(accountId, folderId, 'move'),
    }));
    if (items.length > 0) {
      nodes.push({ kind: 'separator', id: 'sep-file' });
      nodes.push({
        kind: 'submenu',
        id: MOVE_TO_SUBMENU_ID,
        label: 'Move to',
        shortcut: 'V',
        filterLabel: 'Filter folders',
        items,
      });
    }
  } else if (sourceAccounts.size > 1) {
    nodes.push({ kind: 'separator', id: 'sep-file' });
    nodes.push({
      kind: 'item',
      id: MOVE_TO_SUBMENU_ID,
      label: 'Move to…',
      shortcut: 'V',
      disabled: true,
      hint: `Needs one mailbox — these ${messages.length} are from ${sourceAccounts.size}, and a folder belongs to a single account.`,
      onSelect: () => {},
    });
  } else {
    const moveTargets = flattenMoveTargets(folders, currentFolderId);
    if (moveTargets.length > 0) {
      nodes.push({ kind: 'separator', id: 'sep-file' });
      nodes.push({
        kind: 'submenu',
        id: MOVE_TO_SUBMENU_ID,
        label: 'Move to',
        shortcut: 'V',
        filterLabel: 'Filter folders',
        items: moveTargets.map((target) => ({
          kind: 'item' as const,
          id: `move-${target.folder.id}`,
          label: target.folder.name,
          depth: target.depth,
          onSelect: () => actions.move(target.folder.id),
        })),
      });
    }
  }

  /*
   * Copy to… (ADR 0108): the same lists as Move to, the current folder
   * included (a second copy is a real thing to want). It is how a phone
   * copies a selection, having no Ctrl-drag. Absent for a selection from
   * several mailboxes without the transfer: no one folder list fits it.
   */
  let copyItems: MenuActionNode[] = [];
  if (actions.copy !== undefined || transfer !== undefined) {
    if (transfer !== undefined && mailboxes !== undefined && mailboxes.length > 1) {
      copyItems = mailboxMoveItems({
        mailboxes,
        sources: sourceAccounts,
        online,
        offlineReason: OFFLINE_TRANSFER_REASON,
        idPrefix: 'copy',
      }).map(({ accountId, folderId, ...item }) => ({
        kind: 'item' as const,
        ...item,
        onSelect: () => transfer(accountId, folderId, 'copy'),
      }));
    } else if (sourceAccounts.size === 1 && actions.copy !== undefined) {
      const copy = actions.copy;
      copyItems = flattenMoveTargets(folders, undefined).map((target) => ({
        kind: 'item' as const,
        id: `copy-${target.folder.id}`,
        label: target.folder.name,
        depth: target.depth,
        onSelect: () => copy(target.folder.id),
      }));
    }
  }
  if (copyItems.length > 0) {
    nodes.push({
      kind: 'submenu',
      id: COPY_TO_SUBMENU_ID,
      label: 'Copy to',
      filterLabel: 'Filter folders',
      items: copyItems,
    });
  }

  if (actions.moreActions && messages.length === 1) {
    nodes.push(
      { kind: 'separator', id: 'sep-more' },
      { kind: 'item', id: 'more', label: 'More actions…', onSelect: actions.moreActions },
    );
  }
  return nodes;
}
